<template>
  <v-navigation-drawer
    v-model="drawer"
    app
    clipped
    :color="$vuetify.theme.dark ? '#262626' : '#37474F'"
    @update:mini-variant="$emit('input', value)"
  >
    <v-list nav>
      <!-- Favorites Section -->
      <v-list-item v-if="navLinksFavorites.length > 0">
        <v-list-item-content
          class="menu_title"
          @click="toggleFavorites = !toggleFavorites"
        >
          {{ $t('nav.favorites') }}
        </v-list-item-content>
        <v-list-item-icon
          class="toggle"
          @click="toggleFavorites = !toggleFavorites"
        >
          <v-icon color="bright" v-if="toggleFavorites">
            mdi-chevron-left
          </v-icon>
          <v-icon color="bright" v-else>mdi-chevron-right</v-icon>
        </v-list-item-icon>
      </v-list-item>
      <template v-for="navLink in navLinksFavorites">
        <v-tooltip
          right
          :key="'fav-' + navLink.route"
          :disabled="!navLink.description"
        >
          <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
            <v-list-item
              v-show="toggleFavorites"
              class="bright--text ml-3"
              :to="navLink.route"
              link
              v-bind="tooltipAttrs"
              v-on="tooltipOn"
            >
              <v-list-item-icon class="mr-2">
                <v-icon color="amber">mdi-star</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                {{ $t(navLink.text) }}
              </v-list-item-content>
            </v-list-item>
          </template>
          <span>{{ $t(navLink.description) }}</span>
        </v-tooltip>
      </template>
      <v-divider
        v-if="navLinksFavorites.length > 0"
        class="my-2"
        style="border-color: rgba(255, 255, 255, 0.1)"
      />
      <!-- Nav Sections using NavMenuSection component -->
      <nav-menu-section
        v-for="section in navSections"
        :key="section.key"
        :title="$t(section.titleKey)"
        :nav-links="section.links"
        :expanded="sectionToggles[section.key]"
        :favorites="favorites"
        @toggle="sectionToggles[section.key] = !sectionToggles[section.key]"
        @toggle-favorite="toggleFavorite"
      />
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import { MENU_ITEMS, MENU_ROUTE_SETS } from '@/constants/menuItems'
import NavMenuSection from './NavMenuSection.vue'

export default {
  name: 'NavMenu',

  components: {
    NavMenuSection,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    menulist: {
      type: Array,
      required: true,
    },
  },

  data: () => {
    let favorites = []
    try {
      const raw = localStorage.getItem('tms_favorites')
      favorites = raw ? JSON.parse(raw) : []
    } catch (e) {
      favorites = []
      localStorage.removeItem('tms_favorites')
    }
    return {
      drawer: true,
      favorites,
      toggleFavorites: true,
      sectionToggles: {
        settings: false,
        erc20: false,
        erc721: false,
        erc1155: false,
        arena: false,
        board: false,
        metaqData: false,
        logData: false,
      },
      factory: MENU_ITEMS.map((item) => ({ ...item })),
    }
  },
  methods: {
    updateParentDrawer() {
      this.$emit('drawerWatch', this.drawer)
    },
    toggleFavorite(route) {
      const idx = this.favorites.indexOf(route)
      if (idx > -1) {
        this.favorites.splice(idx, 1)
      } else {
        this.favorites.push(route)
      }
      localStorage.setItem('tms_favorites', JSON.stringify(this.favorites))
    },
    isFavorite(route) {
      return this.favorites.includes(route)
    },
  },
  watch: {
    value(newVal) {
      this.drawer = newVal
    },
    drawer(/*newVal, oldVal*/) {
      this.updateParentDrawer()
    },
  },
  computed: {
    // Navigation sections configuration
    navSections() {
      return [
        {
          key: 'settings',
          titleKey: 'nav.settings',
          links: this.navLinksSettings,
        },
        { key: 'erc20', titleKey: 'nav.erc20', links: this.navLinksErc20 },
        { key: 'erc721', titleKey: 'nav.erc721', links: this.navLinksErc721 },
        {
          key: 'erc1155',
          titleKey: 'nav.erc1155',
          links: this.navLinksErc1155,
        },
        { key: 'arena', titleKey: 'nav.arena', links: this.navLinksArena },
        { key: 'board', titleKey: 'nav.board', links: this.navLinksBoard },
        {
          key: 'metaqData',
          titleKey: 'nav.metaq',
          links: this.navLinksMetaqData,
        },
        {
          key: 'logData',
          titleKey: 'nav.logData',
          links: this.navLinksLogData,
        },
      ]
    },
    // 사이드바에 표시되는 모든 route Set
    visibleRoutes() {
      const routes = [
        ...this.navLinksSettings,
        ...this.navLinksErc20,
        ...this.navLinksErc721,
        ...this.navLinksErc1155,
        ...this.navLinksArena,
        ...this.navLinksBoard,
        ...this.navLinksMetaqData,
        ...this.navLinksLogData,
      ].map((item) => item.route)
      return new Set(routes)
    },
    // 즐겨찾기 메뉴
    navLinksFavorites() {
      return this.factory.filter(
        (item) =>
          this.favorites.includes(item.route) &&
          this.visibleRoutes.has(item.route)
      )
    },
    // menulist를 Set으로 변환 (O(1) 조회)
    menulistSet() {
      return new Set(this.menulist)
    },
    // 카테고리별 메뉴 필터링 헬퍼
    filterByCategory() {
      return (categoryKey, requiresPermission = true) => {
        const categorySet = MENU_ROUTE_SETS[categoryKey]
        return this.factory.filter((item) => {
          if (!categorySet.has(item.route)) return false
          if (requiresPermission && !this.menulistSet.has(item.route)) {
            return false
          }
          return true
        })
      }
    },
    // Settings 메뉴
    navLinksSettings() {
      const settingsLinks = this.filterByCategory('settings')
      // Admin 역할인 경우 관리 메뉴 추가
      if (this.isAdmin) {
        const adminLinks = this.factory.filter((item) =>
          MENU_ROUTE_SETS.adminSettings.has(item.route)
        )
        return [...settingsLinks, ...adminLinks]
      }
      return settingsLinks
    },
    // Admin 역할 여부
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    },
    // ERC20 메뉴
    navLinksErc20() {
      return this.filterByCategory('erc20')
    },
    // ERC721 메뉴
    navLinksErc721() {
      return this.filterByCategory('erc721')
    },
    // ERC1155 메뉴
    navLinksErc1155() {
      return this.filterByCategory('erc1155')
    },
    // Board 메뉴
    navLinksBoard() {
      return this.filterByCategory('board')
    },
    // Arena 메뉴
    navLinksArena() {
      return this.filterByCategory('arena')
    },
    // Log Data 메뉴 (metaplanet_token_log 권한 필요)
    navLinksLogData() {
      if (!this.menulistSet.has('/metaplanet_token_log')) {
        return []
      }
      return this.filterByCategory('logData', false)
    },
    // METAQ Data 메뉴 (metaplanet_token_log 권한 필요)
    navLinksMetaqData() {
      if (!this.menulistSet.has('/metaplanet_token_log')) {
        return []
      }
      return this.filterByCategory('metaqData', false)
    },
    // Store getters
    erc20Address() {
      return this.$store.getters['auth/erc20Address']
    },
    erc721Address() {
      return this.$store.getters['auth/erc721Address']
    },
    erc1155Address() {
      return this.$store.getters['auth/erc1155Address']
    },
  },
}
</script>
<style scoped>
.menu_title {
  font-size: 13px;
  font-weight: 500;
  color: white;
  user-select: none;
  cursor: pointer;
  transition: all 0.2s ease;
}
.menu_title:hover {
  color: #b0bec5;
}
.toggle {
  color: white;
  cursor: pointer;
}

/* List Items */
::v-deep .v-list-item {
  margin: 2px 8px !important;
  border-radius: 6px !important;
  min-height: 44px !important;
  transition: all 0.2s ease !important;
}

::v-deep .v-list-item::before {
  opacity: 0 !important;
}

::v-deep .v-list-item:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

::v-deep .v-list-item--active {
  background: rgba(255, 255, 255, 0.15) !important;
}

::v-deep .v-list-item--active .v-icon {
  color: #fff !important;
}

::v-deep .v-list-item--active .v-list-item-content {
  color: #fff !important;
}

/* List Item Content */
::v-deep .v-list-item-content {
  font-size: 14px;
  font-weight: 400;
}

/* Scrollbar */
::v-deep .v-navigation-drawer__content::-webkit-scrollbar {
  width: 6px;
}

::v-deep .v-navigation-drawer__content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

::v-deep .v-navigation-drawer__content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}
</style>
